import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { PolicyService } from './../../../policy.service';
import { FormService } from './../../../services/shared/form.service';
import { TreeService } from '@modules/policy/services/location/tree.service';
import { BehaviorSubject, of, combineLatest, Observable } from 'rxjs';
import { SaveService } from '@modules/policy/services/shared/save.service';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';

import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import Coverage, { CoverageInfo } from '@shared/models/coverage.model';
import { FormArray, FormBuilder, FormGroup, AbstractControl, Validators } from '@angular/forms';
import LocationTabs from '@shared/models/location.model';
import { CanComponentDeactivate } from '@modules/policy/guards/save.guard';
import { first } from 'rxjs/operators';
import { FormSaveHelper } from '@modules/policy/services/shared/save';
import { PremiumSyncService } from '@modules/policy/services/shared/premium-sync.service';
import moment from 'moment';
import { coverageRequiredByValuesWrapper } from 'collabor8-funcs';

@Component({
  selector: 'app-coverage-table',
  templateUrl: './coverage-table.component.html',
  styleUrls: ['./coverage-table.component.scss']
})

export class CoverageTableComponent implements CanComponentDeactivate {
  primaryDisplayedColumns = ['name', 'limit', 'deductible', 'basisOfSettlement'];
  additionalDisplayedColumns = ['value', 'name', 'limit', 'deductible'];
  data;
  primaryRows: FormArray;
  additionalRows: FormArray;
  primaryForm: FormGroup;
  additionalForm: FormGroup;
  primaryCoverageDataSource: BehaviorSubject<AbstractControl[]>;
  additionalCoverageDataSource: BehaviorSubject<AbstractControl[]>;
  dataSource: MatTreeNestedDataSource<LocationTabs>;
  tables: any[];
  form: FormGroup;
  tabState;
  packageType: string;
  policyCode: string;
  formTypeCd: string;
  province: string;
  readOnlyPolicy: boolean;

  private fieldSubscriptions = [];

  @Input() subtab: string;
  @Input() tab: string;
  @Input() nodeID: number;
  @Input() policyID: number;
  @Input() isChild: boolean;
  @Input() additionalDependencies: { [s: string]: AbstractControl };
  @Input() detailsFormObservable: Observable<Object> = new Observable<Object>();
  @Output() newFormEvent = new EventEmitter<FormGroup>();
  @Output() buildDetailsPayload = new EventEmitter<{ onResult: Function }>();

  readonly ADDITIONAL_COVERAGE_DEPENDENCIES = {
    "ScheduleItemDeductibleCd": {
      type: "deductible",
      dependentCoverageCodes: [
        "EBMB",
        "EP-COVY",
        "EP-FME",
        "FLHP",
        "FTL",
        "HPRO",
        "LUEX",
        "PDCC",
        "PROFLT_ATV",
        "PROFLT_FTC",
        "FPSOLAR",
        "FPDRONE"
      ]
    },
    "ScheduleItemValueAmt": {
      type: "limit",
      dependentCoverageCodes: [
        "EP-FME"
      ]
    }
  }

  constructor(
    private PolicyDataService: PolicyDataService,
    private SaveService: SaveService,
    private snackbar: MatSnackBar,
    private fb: FormBuilder,
    private TreeService: TreeService,
    private formService: FormService,
    private policyService: PolicyService,
    private FormSaveHelper: FormSaveHelper,
    private premiumSyncService: PremiumSyncService,
  ) { }

  get transactionType() {
    return this.PolicyDataService.getTransactionType()
  }

  saveBeforeDeactivate(detailsForm?: FormGroup): Promise<any> {
    if (!this.form) {
      return of(null).toPromise();
    }
    if (this.form.dirty || this.form.touched || detailsForm) {
      return this.save(detailsForm);
    }
    if (!this.form.valid) {
      return of(null).toPromise();
    }
    const cascadingIds = {
      nodeState: [parseInt(String(this.nodeID)), true]
    };
    return this.FormSaveHelper.syncNavigationState(this.policyID.toString(), cascadingIds);
  }

  newForm(form: FormGroup) {
    this.newFormEvent.emit(form);
  }

  ngOnInit() { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.nodeID && this.policyID && this.subtab && this.tab) {
      this.init();
    }
    if (changes.additionalDependencies != null) {
      this.createAdditionalDependencySubscriptions();
    }
  }

  ngOnDestroy() {
    this.destroySubscriptions();
  }

  destroySubscriptions() {
    while (this.fieldSubscriptions.length > 0) {
      this.fieldSubscriptions.shift().unsubscribe();
    }
  }

  init() {
    this.matTreeSubscription();
    this.initializeTableData();
    this.initFormSubscription();
    const stateObs = this.PolicyDataService.stateSubject;
    const dataObs = this.PolicyDataService.getData(this.policyID, this.nodeID);
    const columnObs = this.PolicyDataService.getCoverageMetaData(this.PolicyDataService.getPolicyID());
    const readOnlyObs = this.PolicyDataService.policyReadOnly;
    combineLatest([stateObs, dataObs, columnObs, readOnlyObs])
      .pipe(first())
      .subscribe(res => {
        const [states, data, columnMetaData, readOnly] = res;
        this.readOnlyPolicy = readOnly
        this.additionalDisplayedColumns = columnMetaData["additionalColumns"];
        this.primaryDisplayedColumns = columnMetaData["primaryColumns"]
        this.tabState = states[this.tab];
        this.PolicyDataService.updateUnderWritingRules(data["opinions"]);
        this.data = data["data"];
        this.province = data["data"].meta?.Product?.Province;
        if (this.data["RiskPackageTypeCd"]) {
          this.packageType = this.data["RiskPackageTypeCd"];
          this.policyCode = this.data["RiskPackageCd"];
        } else if (this.data["LiabilityPackageTypeCd"]) {
          this.packageType = this.data["LiabilityPackageTypeCd"];
          this.policyCode = this.data["LiabilityPackageTypeCd"];
        } else if (this.data["ScheduleItemFormTypeCd"]) {
          this.formTypeCd = this.data["ScheduleItemFormTypeCd"]
        } else if (this.data["OutbuildingFormCd"]) {
          this.formTypeCd = this.data["OutbuildingFormCd"]
        } else if (this.data["BinsSiloFormTypeCd"]) {
          this.formTypeCd = this.data["BinsSiloFormTypeCd"]
        } else if (this.data["VehicleTypeCd"]) {
          this.packageType = this.data["VehicleTypeCd"];
          this.policyCode = this.data["VehicleTypeCd"];
        } else {
          this.packageType = "";
          this.policyCode = "";
        }
        this.form = this.fb.group({
          'primary': this.primaryForm,
          'additional': this.additionalForm
        });
        this.data.PrimaryCoverages.forEach((coverage: CoverageInfo) => this.addRow(coverage, "PRIMARY", true));
        this.data.AdditionalCoverages.forEach((coverage: CoverageInfo) => this.addRow(coverage, "ADDITIONAL", true));
        this.updateView();
        this.form.markAsPristine();
        this.setForm(this.form, this.subtab, this.tab, this.nodeID);
        this.newForm(this.form);
        if (this.tab == "location") {
          this.handleValidations();
        }
        for (let form in this.form.controls) { // run logic on each coverage after all have been initialized
          this.form.get(form).get("Coverages")["controls"].forEach(row => this.handleMandatoryOptional(row, true));
        }
        if (this.premiumSyncService.isPremiumSyncEnabled) {
          this.getQuickQuotePremium();
        }
      });
  }

  initFormSubscription() {
    this.detailsFormObservable.subscribe(data => {
      this.data = { ...this.data, ...data };
      if (!this.form) return; // Guard: form may not be initialized yet
      for (let form in this.form.controls) {
        this.form.get(form).get("Coverages")["controls"].forEach(row => {
          // Re-validate requiredBy when vehicle data changes
          // The validator's getter function will pick up the updated this.data
          if (row.get('__meta').value.requiredBy) {
            const coverageSelectedControl = row.get('CoverageSelectedInd');
            coverageSelectedControl.updateValueAndValidity();
            if (!coverageSelectedControl.value) {
              coverageSelectedControl.markAsTouched();
            }
          }
          this.handleMandatoryOptional(row, true);
          this.handleSpecialDefaults(row, true);
        });
      }
    });
  }

  addRow(coverage: CoverageInfo, type: string, noUpdate?: boolean) {
    const row = this.fb.group(coverage);
    if (this.readOnlyPolicy) {
      row.disable()
    }
    if (row.get('__meta').value.Limit) {
      row.get('__meta').value.Limit.Visible = row.get("CoverageSelectedInd").value;
    }
    if (row.get('__meta').value.Deductible) {
      row.get('__meta').value.Deductible.Visible = row.get("CoverageSelectedInd").value;
    }
    
    // Add validator based on requiredBy configuration in __meta
    if (row.get('__meta').value.requiredBy) {
      const coverageSelectedControl = row.get('CoverageSelectedInd');
      const existingValidator = coverageSelectedControl.validator;
      
      coverageSelectedControl.setValidators(Validators.compose([
        existingValidator,
        coverageRequiredByValuesWrapper(
          () => {
            const requiredBy = row.get('__meta').value.requiredBy;
            return requiredBy.fieldName && this.data ? this.data[requiredBy.fieldName] : null;
          },
          row.get('__meta').value.requiredBy
        )
      ]));
      
      // coverageSelectedControl.updateValueAndValidity();
      if (!coverageSelectedControl.value) {
        coverageSelectedControl.markAsTouched();
      }
    }
    
    if (this.shouldDisable(row, "Deductible")) {
      row.get('CoverageDeductibleAmt').disable();
    }
    if (this.shouldDisable(row, "Limit")) {
      row.get("CoverageLimitAmt").disable();
    }
    if (row.get('CoverageLimitAmt').enabled) {
      this.onCheck(row, { checked: row.get('CoverageSelectedInd').value }, true, undefined, true);
    }
    if (type === "PRIMARY") {
      this.primaryRows.push(row);
    }
    if (type === "ADDITIONAL") {
      this.additionalRows.push(row);
    }
    if (!noUpdate) {
      this.updateView();
    }
  }

  updateView() {
    this.primaryCoverageDataSource.next(this.primaryRows.controls);
    this.additionalCoverageDataSource.next(this.additionalRows.controls);
  }

  buildCoveragesPayload(): Object {
    const Coverages: Array<Record<string, unknown>> = [];
    const payload = {
      VehicleID: this.data.VehicleID,
      Province: this.data.meta?.Product?.Province,
      Coverages
    };
    for (let form in this.form.controls) {
      this.form.get(form).get("Coverages")["controls"].forEach((row: FormGroup) => {
        const raw = row.getRawValue() as Record<string, unknown>;
        if (!raw.CoverageSelectedInd) return;
        Coverages.push({
          CoverageNameCd: raw.CoverageNameCd,
          CoverageSelectedInd: raw.CoverageSelectedInd,
          CoverageLimitAmt: raw.CoverageLimitAmt,
          CoverageDeductibleAmt: raw.CoverageDeductibleAmt,
          CoverageTypeCd: raw.CoverageTypeCd
        });
      });
    }

    return payload;
  }

  getQuickQuotePremium() {
    this.buildDetailsPayload.emit({ onResult: ({ formData }) => {
      this.premiumSyncService.syncPremium({ ...this.buildCoveragesPayload(), formData });
    }});
  }

  limitChanged(row: FormGroup, event: Event) {
    const covName = row.get("CoverageNameCd").value;
    this.handleLimitSubscriptionDefaults(row, covName);
  }

  deductibleChanged(row: FormGroup, event: Event) {
    const covName = row.get("CoverageNameCd").value;
    this.handleLimitSubscriptionDefaults(row, covName);
  }

  updateCoverage(row: FormGroup, e: { checked: boolean }, initial?: boolean, applyDefaults?: boolean) {
    const checked = e.checked;
    row.get("CoverageSelectedInd").patchValue(checked, { emitEvent: false });
    const covName = row.get("CoverageNameCd").value;
    this.onCheck(row, { checked }, initial, applyDefaults, true);
    this.handleMandatoryOptional(row);
    this.handleLimitSubscriptionDefaults(row, covName);
    switch (row.get("CoverageNameCd").value) {
      case "E15GAR":
        // For homeowners: E15GAR and E27CSL are mutually inclusive
        if (this.packageType && this.packageType.startsWith('HO-')) {
          if (checked) {
            // If E15GAR is selected, also select E27CSL
            this.checkCoverage("E27CSL", false);
          } else {
            // If E15GAR is deselected, also deselect E27CSL
            this.uncheckCoverage("E27CSL");
          }
        }
        break;
      case "E27CSL":
        // For homeowners: E15GAR and E27CSL are mutually inclusive
        if (this.packageType && this.packageType.startsWith('HO-')) {
          if (checked) {
            // If E27CSL is selected, also select E15GAR
            this.checkCoverage("E15GAR", false);
          } else {
            // If E27CSL is deselected, also deselect E15GAR
            this.uncheckCoverage("E15GAR");
          }
        }
        // For seasonal homeowners: E27CSL and E42RCP are mutually inclusive
        if (this.packageType && (this.packageType.startsWith('SEAR-') || this.packageType.startsWith('SEAH-'))) {
          if (checked) {
            // If E27CSL is selected, also select E42RCP
            this.checkCoverage("E42RCP", false);
          } else {
            // If E27CSL is deselected, also deselect E42RCP
            this.uncheckCoverage("E42RCP");
          }
        }
        break;
      case "E42RCP":
        // For seasonal homeowners: E27CSL and E42RCP are mutually inclusive
        if (this.packageType && (this.packageType.startsWith('SEAR-') || this.packageType.startsWith('SEAH-'))) {
          if (checked) {
            // If E42RCP is selected, also select E27CSL
            this.checkCoverage("E27CSL", false);
          } else {
            // If E42RCP is deselected, also deselect E27CSL
            this.uncheckCoverage("E27CSL");
          }
        }
        break;
      case "RHG":
        // For SK Auto: When RHG is enabled, auto-select COL and CMP with $700 deductible
        if (this.province === "SK" && checked) {
          this.checkCoverage("COL", false);
          this.checkCoverage("CMP", false);
          this.getCoverage("COL")?.get("CoverageDeductibleAmt").setValue(700);
          this.getCoverage("CMP")?.get("CoverageDeductibleAmt").setValue(700);
        }
        break;
    }
    this.getQuickQuotePremium();
  }

  onCheck(row: FormGroup, { checked }, initial?: boolean, applyDefaults?: boolean, suppressQuickQuote?: boolean) {
    // Re-validate if coverage has requiredBy configuration
    if (row.get('__meta').value.requiredBy) {
      row.get('CoverageSelectedInd').updateValueAndValidity();
    }
    
    if (checked) {
      if (row.get('__meta').value.Limit != null) {
        row.get('__meta').value.Limit.Visible = true;
      }
      if (row.get('__meta').value.Deductible != null) {
        row.get('__meta').value.Deductible.Visible = true;
      }
      if (!initial || applyDefaults) row.enable({ emitEvent: false });
      this.handleSpecialDefaults(row, initial ? false : true);

      if (row.get('__meta').value.Limit) {
        row.get("CoverageLimitAmt").setValidators([Validators.required]);
        if (this.shouldDisable(row, "Limit")) {
          row.get('CoverageLimitAmt').disable();
        }
        if ((!initial || applyDefaults) && row.get("CoverageLimitAmt").value === null) {
          row.get("CoverageLimitAmt").setValue(row.get('__meta').value.Limit.Default);
        }
        row.get("CoverageLimitAmt").updateValueAndValidity();
        row.get("CoverageLimitAmt").markAsTouched();
      }

      if (row.get('__meta').value.Deductible) {
        if (this.shouldDisable(row, "Deductible")) {
          row.get("CoverageDeductibleAmt").disable();
        }
        if ((!initial || applyDefaults) && !row.get("CoverageDeductibleAmt").value) {
          row.get("CoverageDeductibleAmt").setValue(row.get('__meta').value.Deductible.Default);
        }
        row.get("CoverageDeductibleAmt").updateValueAndValidity();
        row.get("CoverageDeductibleAmt").markAsTouched();
      }
    } else {
      if (row.get('__meta').value.Limit != null) {
        row.get('__meta').value.Limit.Visible = false;
      }
      if (row.get('__meta').value.Deductible != null) {
        row.get('__meta').value.Deductible.Visible = false;
      }
      row.setValidators(null);
      row.get('CoverageDeductibleAmt').reset();
      row.get('CoverageLimitAmt').reset();
  
      row.get("CoverageDeductibleAmt").disable();
      row.get("CoverageLimitAmt").disable();
      row.updateValueAndValidity();
    }

    if (!suppressQuickQuote) {
      this.getQuickQuotePremium();
    }
  }

  /**
   * Toggles a coverage to a given value (checks / unchecks)
   * @param covName the name of the coverage to toggle
   * @param value the value to set the coverage to (true / false)
   */
  toggleCoverage(covName: string, value: boolean) {
    const row = this.getCoverage(covName);
    row.get("CoverageSelectedInd").setValue(value, { emitEvent: false });
    this.onCheck(row, { checked: value }, false);
  }

  /**
   * Gets the row of the given coverage.
   * @param covName the name of the coverage to look for.
   * @returns the row of the found coverage; "undefined" if none found.
   */
  getCoverage(covName: string) {
    for (let form in this.form.controls) {
      const coverage = this.form.get(form).get("Coverages")["controls"].find(coverage => coverage.get("CoverageNameCd").value == covName);
      if (coverage) {
        return coverage;
      }
    }
    return undefined;
  }

  /**
   * Deselects the coverage and disables the checkbox to prevent the user from re-enabling it.
   * @param covName the name of the coverage
   */
  clearAndDisable(covName: string) {
    const cov = this.getCoverage(covName);
    if (!cov) return;
    cov.get("CoverageSelectedInd").setValue(false, { emitEvent: false });
    this.onCheck(cov, { checked: false }, false);
    cov.get("CoverageSelectedInd").disable({ emitEvent: false });
  }

  /**
   * Checks the coverage and disables the checkbox to prevent the user from unchecking it.
   * @param covName the name of the coverage
   */
  checkAndDisable(covName: string) {
    const cov = this.getCoverage(covName);
    if (!cov) return;
    this.checkCoverage(covName);
    cov.get("CoverageSelectedInd").disable({ emitEvent: false });
  }

  /**
   * Sets the coverage to unchecked.
   * @param covName the name of the coverage
   */
  uncheckCoverage(covName: string, onLoad: boolean = false) {
    if (onLoad) return;
    const cov = this.getCoverage(covName);
    if (!cov) return;
    cov.get("CoverageSelectedInd").setValue(false, { emitEvent: false });
    this.onCheck(cov, { checked: false }, false);
  }

  /**
   * Sets the coverage to checked.
   * @param covName the name of the coverage
   */
  checkCoverage(covName: string, initial = false) {
    const cov = this.getCoverage(covName);
    if (!cov) return;
    cov.get("CoverageSelectedInd").setValue(true, { emitEvent: false });
    this.onCheck(cov, { checked: true }, initial, true);
  }

  /**
   * Checks if the vehicle is licensed.
   * @returns true if the vehicle is licensed, false otherwise.
   */
  isVehicleLicensed() {
    if (this.policyCode == "PPT" && this.data["VehiclePPTTypeOfUseCd"] && !["FARMU", "UNLIC"].includes(this.data["VehiclePPTTypeOfUseCd"])) return true;
    return false;
  }

  /**
   * Gets the limit value of the given coverage.
   * @param covName the name of the coverage to look for.
   * @returns the limit value of the coverage; 0 if none found.
   */
  getLimit(covName: string): number {
    const coverage = this.getCoverage(covName);
    if (coverage != null && coverage.get("CoverageLimitAmt").value != null) {
      return parseInt(coverage.get("CoverageLimitAmt").value);
    } else {
      return -1;
    }
  }

  /**
   * Gets the limit value for one of an array of coverages that may exist on the page
   * Searches coverages in the order provided, returns the first (if any) coverage with a limit value
   * @param possibleFields An array of coverage names that may be on the page
   * @returns the limit value of the coverage that exists; 0 if none found & the name of the coverage
   */
  getLimitFromPossibleArray(possibleFields: string[]): { limitDefault: number, foundField: string } {
    let limitDefault = 0;
    let field;
    while (possibleFields.length > 0 && limitDefault == 0) {
      field = possibleFields.shift();
      limitDefault = this.getLimit(field);
    }
    return {
      limitDefault,
      foundField: field
    };
  }

  /**
   * Gets the deductible value of the given coverage.
   * @param covName the name of the coverage to look for.
   * @returns the deductible value of the coverage; 0 if none found.
   */
  getDeductible(covName: string): number {
    const coverage = this.getCoverage(covName);
    if (coverage != null && coverage.get("CoverageDeductibleAmt").value != null) {
      return parseInt(coverage.get("CoverageDeductibleAmt").value);
    } else {
      return -1;
    }
  }

  /**
   * Gets the deductible value for one of an array of coverages that may exist on the page
   * Searches coverages in the order provided, returns the first (if any) coverage with a deductible value
   * @param possibleFields An array of coverage names that may be on the page
   * @returns the deductible value of the coverage that exists; 0 if none found & the name of the coverage
   */
  getDeductibleFromPossibleArray(possibleFields: string[]): { deductible: number, foundField: string } {
    let deductible = 0;
    let field;

    while (possibleFields.length > 0 && deductible <= 0) {
      field = possibleFields.shift();
      deductible = this.getDeductible(field);
    }

    return {
      deductible,
      foundField: field
    };
  }

  /**
   * Sets the limit of the given coverage to the given value. If value is outside of the min or max, sets the value
   * to the min or max allowed value instead.
   * @param overwrite whether to overwrite the value already in row.
   * @param row the row that the coverage belongs to.
   * @param value the value to set the limit to.
   * @param min if the coverage has a minimum allowed value, set it here. Leave as "undefined" otherwise.
   * @param max if the coverage has a maximum allowed value, set it here. Leave as "undefined" otherwise.
   */
  setLimitDefault(overwrite: boolean, row: FormGroup, value: number, min: number = 0, max: number = value) {
    if (row == undefined || !row.get("CoverageSelectedInd").value || value < 0) return;
    const output = Math.round(Math.max(Math.min(max, value), min));
    if (row.get("CoverageLimitAmt").value != output && row.get("CoverageSelectedInd").value &&
      (overwrite || row.get("CoverageLimitAmt").value == null)) row.get("CoverageLimitAmt").setValue(output);
  }

  /**
   * Sets the deductible of the given coverage to the given value. If value is outside of the min or max, sets the value
   * to the min or max allowed value instead.
   * @param row the row that the coverage belongs to.
   * @param value the value to set the deductible to.
   * @param min if the coverage has a minimum allowed value, set it here. Leave as "undefined" otherwise.
   * @param max if the coverage has a maximum allowed value, set it here. Leave as "undefined" otherwise.
   */
  setDeductibleDefault(row: FormGroup, value: number, min: number = 0, max: number = value) {
    if (row == undefined || !row.get("CoverageSelectedInd").value || value < 0) return;
    const currentDeductible = row.get("CoverageDeductibleAmt").value
    const deductibleStatus = row.get("CoverageDeductibleAmt").status
    const output = Math.round(Math.max(Math.min(max, value), min));
    if (deductibleStatus == "DISABLED" || (currentDeductible == null && currentDeductible != output && row.get("CoverageSelectedInd").value)) {
      row.get("CoverageDeductibleAmt").setValue(output);
    }
  }

  /**
   * Sets the deductible of the given coverage to the given value, overwriting any existing value.
   * Use when a coverage's deductible must always match another coverage's deductible.
   * @param row the row that the coverage belongs to.
   * @param value the value to set the deductible to.
   */
  syncDeductible(row: FormGroup, value: number) {
    if (row == undefined || !row.get("CoverageSelectedInd").value || !(value > 0)) return;
    if (row.get("CoverageDeductibleAmt").value != value) row.get("CoverageDeductibleAmt").setValue(value);
  }

  shouldDisable(row: FormGroup, type: string): boolean {
    return (row.get('__meta').value[type] && row.get('__meta').value[type].Editable === false);
  }

  save(detailsForm?: FormGroup): Promise<any> {
    if (this.form.disabled) return
    let savePayload = {
      Coverages: [
        ...this.primaryForm.getRawValue()["Coverages"],
        ...this.additionalForm.getRawValue()["Coverages"]
      ]
    }
    for (let coverage of savePayload.Coverages) {
      if (coverage.CoverageLimitAmt === '') {
        coverage.CoverageLimitAmt = null;
      }
      if (coverage.CoverageDeductibleAmt === '') {
        coverage.CoverageDeductibleAmt = null;
      }
    }
    const nodeStatePayload: object = {
      nodeState: [parseInt(String(this.nodeID)), this.form.valid]
    };
    if (detailsForm) {
      const detailsFormPayload = this.FormSaveHelper.buildSavePayload(detailsForm, nodeStatePayload);
      savePayload = {
        ...savePayload,
        ...detailsFormPayload["payload"]["data"]
      };
    }
    const policySaveReq = this.SaveService.updatePolicy(this.policyID, savePayload, this.nodeID).toPromise();
    const statePayload = this.FormSaveHelper.buildStatePayload(nodeStatePayload);
    const stateSaveReq = this.SaveService.updateStates(this.policyID, statePayload).toPromise();
    return Promise.all([policySaveReq, stateSaveReq]).then((res) => {
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.showSaveSnackbar();
      this.getQuickQuotePremium()
    });
  }

  showSaveSnackbar() {
    this.snackbar.open("Saved", null, {
      duration: 2000,
    });
  }

  updateNavigationTree() {
    this.PolicyDataService.getPolicyStates(this.policyID).subscribe(tree => {
      this.PolicyDataService.updateStates(tree);
      this.tabState = tree[this.tab];
    })
  }

  matTreeSubscription() {
    this.dataSource = new MatTreeNestedDataSource<LocationTabs>();
    this.TreeService.dataSourceSubject.subscribe(data => {
      this.dataSource.data = null;
      this.dataSource.data = data;
    });
  }

  initializeTableData() {
    this.data = {
      "PrimaryCoverages": [],
      "AdditionalCoverages": []
    };
    this.primaryRows = this.fb.array([]);
    this.additionalRows = this.fb.array([]);
    this.primaryForm = this.fb.group({ "Coverages": this.primaryRows });
    this.additionalForm = this.fb.group({ "Coverages": this.additionalRows })
    this.primaryCoverageDataSource = new BehaviorSubject<AbstractControl[]>([]);
    this.additionalCoverageDataSource = new BehaviorSubject<AbstractControl[]>([]);
    this.tables = [
      { type: "Primary", label: "Primary Coverages", data: this.primaryCoverageDataSource, form: this.primaryForm },
      { type: "Additional", label: "Additional Coverages", data: this.additionalCoverageDataSource, form: this.additionalForm }
    ];
  }

  onBlurNumber(control: AbstractControl) {
    if (control.value != "" && control.value != null) {
      control.setValue(Number(control.value));
    } else {
      control.setValue(null);
    }
  }

  setForm(form: FormGroup, subtab: string, tab: string, nodeID: number) {
    if (!this.isChild) {
      this.formService.setForm({
        form,
        orderedFormJson: [],
        formid: nodeID
      });
    }
  }

  // COVERAGE RISK DETAILS VALIDATIONS --- MOVE THIS

  handleValidations() {
    this.primaryForm = this.form.get('primary') as FormGroup;
    this.additionalForm = this.form.get('additional') as FormGroup;
    this.TreeService.appendFormToData(this.dataSource.data, this.nodeID, this.form);

    const formValuesPrimary: CoverageInfo[] = this.primaryForm.getRawValue()["Coverages"];
    const formValuesAdditional: CoverageInfo[] = this.additionalForm.getRawValue()["Coverages"];

    const EQD_Index = formValuesAdditional.findIndex(cov => cov.CoverageNameCd == "EQ-D");
    const EQB_Index = formValuesAdditional.findIndex(cov => cov.CoverageNameCd == "EQ-B");
    const EQC_Index = formValuesAdditional.findIndex(cov => cov.CoverageNameCd == "EQ-C");

    const COVD_Index = formValuesPrimary.findIndex(cov => cov.CoverageNameCd == "COVD");
    const COVB_Index = formValuesPrimary.findIndex(cov => cov.CoverageNameCd == "COVB");
    const COVC_Index = formValuesPrimary.findIndex(cov => cov.CoverageNameCd == "COVC");

    this.form.valueChanges.subscribe((res) => {
      const { primary, additional } = res;

      if (EQD_Index !== -1 && COVD_Index !== -1) {
        this.evaluateField(primary.Coverages[COVD_Index], additional.Coverages[EQD_Index], "COVD", "EQ-D");
      }

      if (EQB_Index !== -1 && COVB_Index !== -1) {
        this.evaluateField(primary.Coverages[COVB_Index], additional.Coverages[EQB_Index], "COVB", "EQ-B");
      }

      if (EQC_Index !== -1 && COVC_Index !== -1) {
        this.evaluateField(primary.Coverages[COVC_Index], additional.Coverages[EQC_Index], "COVC", "EQ-C");
      }

      this.handleMinMax();
    })
  }

  evaluateField(primaryCoverages, additionalCoverages, coverageTypeA, coverageTypeB) {
    const result = this.AcannotExceedB(additionalCoverages ? additionalCoverages["CoverageLimitAmt"] : null,
      primaryCoverages ? primaryCoverages["CoverageLimitAmt"] : null);
    const rowB = this.additionalRows.controls.find((fg: FormGroup) => fg.get('CoverageNameCd').value == coverageTypeB);
    const rowA = this.primaryRows.controls.find((fg: FormGroup) => fg.get('CoverageNameCd').value == coverageTypeA);
    const control = rowB.get('CoverageLimitAmt');
    this.setErrors(control, result, `Cannot exceed the ${rowA.get('CoverageNameNm').value} limit`);
  }

  setErrors(control: AbstractControl, result: boolean, message: string) {
    if (result === false) {
      control.setErrors({ message });
    } else {
      this.removeError(control, "message");
    }
  }

  AcannotExceedB(a, b) {
    if (a == null || b == null) return true;
    return a > b ? false : true;
  }

  // this function removes single error
  removeError(control: AbstractControl, error: string) {
    const err = control.errors;
    if (err) {
      delete err[error];
      if (!Object.keys(err).length) {
        control.setErrors(null);
      } else {
        control.setErrors(err);
      }
    }
  }

  /**
   * Sets the minimum and maximum allowed value for a coverage. Can be called in place of {@link setMin} and
   * {@link setMax} by simply setting one of the limits to "undefined".
   * @param covName the name of the coverage.
   * @param min the minimum allowed value; can be a number or "undefined" if none.
   * @param max the maximum allowed value; can be a number, or "undefined" if none.
   * @param field the field to set the min/max allowed value for; "CoverageLimitAmt" (default) or "CoverageDeductibleAmt."
   */
  setMinMax(covName: string, min: number | undefined, max: number | undefined, field: string = "CoverageLimitAmt") {
    const cov = this.getCoverage(covName);
    if (cov == undefined || cov.get(field).value == null) {
      return;
    }
    min = (min == undefined) ? undefined : Math.round(min);
    max = (max == undefined) ? undefined : Math.round(max);
    if (min != undefined && max == undefined) {
      this.setMin(covName, min, field);
    } else if (min == undefined && max != undefined) {
      this.setMax(covName, max, field);
    } else {
      this.setErrors(
        cov.get(field),
        cov.get("CoverageSelectedInd").value ? (
          cov.get(field).value >= min &&
          cov.get(field).value <= max
        ) : true,
        "Value must be between " + min.toLocaleString("en-US") + " and " + max.toLocaleString("en-US") + "."
      )
    }
  }

  /**
   * Sets the minimum allowed value for a coverage.
   * @param covName the name of the coverage.
   * @param min the minimum allowed value; can be a number.
   * @param field the field to set the min/max allowed value for; "CoverageLimitAmt" (default) or "CoverageDeductibleAmt."
   */
  setMin(covName: string, min: number, field: string | undefined = "CoverageLimitAmt") {
    const cov = this.getCoverage(covName);
    if (cov == undefined || cov.get(field).value == null) return;
    this.setErrors(
      cov.get(field),
      cov.get("CoverageSelectedInd").value ? (
        cov.get(field).value >= min
      ) : true,
      "Value cannot be less than " + min.toLocaleString("en-US") + "."
    )
  }

  /**
   * Sets the maximum allowed value for a coverage.
   * @param covName the name of the coverage.
   * @param min the maximum allowed value; can be a number.
   * @param field the field to set the min/max allowed value for; "CoverageLimitAmt" (default) or "CoverageDeductibleAmt."
   */
  setMax(covName: string, max: number, field: string | undefined = "CoverageLimitAmt") {
    const cov = this.getCoverage(covName);
    if (cov == undefined || cov.get(field).value == null) return;
    this.setErrors(
      cov.get(field),
      cov.get("CoverageSelectedInd").value ? (
        cov.get(field).value <= max
      ) : true,
      "Value cannot be greater than " + max.toLocaleString("en-US") + "."
    )
  }

  /**
   * Create appropriate subscriptions when additional dependencies are passed to the coverage table component
   */
  createAdditionalDependencySubscriptions(): void {
    this.destroySubscriptions();

    if (this.additionalDependencies != null) {
      for (const dependency in this.additionalDependencies) {
        if (this.additionalDependencies[dependency] == null) continue;
        const fieldSubscription = this.additionalDependencies[dependency].valueChanges.subscribe((value) => {
          const dependentCoverageObject = this.ADDITIONAL_COVERAGE_DEPENDENCIES[dependency];
          for (const dependentCoverageName of dependentCoverageObject.dependentCoverageCodes) {
            const dependentCoverage = this.getCoverage(dependentCoverageName);
            if (dependentCoverage != null) {
              if (dependentCoverageObject.type === 'deductible') {
                this.setDeductibleDefault(dependentCoverage, value);
              } else if (dependentCoverageObject.type === 'limit') {
                this.setLimitDefault(true, dependentCoverage, value);
              }
            }
          }
        });
        this.fieldSubscriptions.push(fieldSubscription);
      }
    }
  }

  /**
   * Checks if a optional coverage should have its checkbox disabled (and therefore treated as mandatory / forbidden)
   * @param row the row to check for
   */
  handleMandatoryOptional(row: FormGroup, onLoad: boolean = false) {
    const covName = row.get("CoverageNameCd").value;
    const checked = row.get("CoverageSelectedInd").value;
    row.get("CoverageSelectedInd").enable({ emitEvent: false }); // Don't emit event to prevent infinite loop
    switch (covName) {
      case "SEF5":
        if (this.policyCode == "PPV" && this.data.VehicleOwnedLeasedCd == "L") {
          this.checkAndDisable(covName);
        }
        break;
      case "SEF13S":
        if (["PPV", "ACS"].includes(this.policyCode) && this.data.VehicleGlassDamagedInd) {
          this.checkAndDisable(covName);
        }
        break;
      case "SEF30":
        if (this.policyCode == "PPV" && this.data.VehicleAttachedMachineryInd) {
          this.checkAndDisable(covName);
        }
        break;
      case "AUVLU":
        if (this.getCoverage("COL")?.get("CoverageSelectedInd").value && this.getCoverage("CMP")?.get("CoverageSelectedInd").value && this.policyCode == "PPV") {
          this.checkAndDisable(covName);
        }
        if (!checked) {
          if (["TRA"].includes(this.policyCode) && this.data["VehicleTRATypeOfUseCd"] == "TC") {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("COL");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("CMP", onLoad);
            this.uncheckCoverage("COL", onLoad);
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("COL");
            this.uncheckCoverage("AUVLU");
            this.uncheckCoverage("AUDEV");
            this.uncheckCoverage("SEF19A");
          }
        } else {
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("AUVLU");
            if (!this.getCoverage("SEF19A")?.get("CoverageSelectedInd").value) this.checkCoverage("AUDEV");
          }
        }
        break;
      case "EEF19":
        if (["MOT", "MOL", "MOU", "VST"].includes(this.policyCode) && this.data["VehicleCommercialLicenseInd"]) {
          this.clearAndDisable(covName);
        }
        if (this.policyCode == "PPT" && this.isVehicleLicensed()) {
          this.clearAndDisable(covName);
        }
        if (this.policyCode == "PPT" && !this.isVehicleLicensed()) {
          this.checkAndDisable(covName);
        }
        if (checked) {
          this.clearAndDisable("EEF19A");
        } else {
          this.getCoverage("EEF19A")?.get("CoverageSelectedInd").enable({ emitEvent: false });
          if (this.getCoverage("EEF19A")?.get("CoverageSelectedInd").value) {
            this.clearAndDisable(covName);
          }
        }
        break;
      case "EEF19A":
        if (checked) {
          this.clearAndDisable("EEF19");
        } else {
          this.getCoverage("EEF19")?.get("CoverageSelectedInd").enable({ emitEvent: false });
          if (this.getCoverage("EEF19")?.get("CoverageSelectedInd").value) {
            this.clearAndDisable(covName);
          }
        }
        break;
      case "AUVPL":
        if (this.province !== "MB") break;
        if (this.data.VehicleOwnedLeasedCd === "L") {
          this.clearAndDisable(covName);
        }
        if (checked) {
          if (["PPV"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("TP");
            this.checkCoverage("AUVLU");
          }
        }
        break;
      case "AUVPS":
        if (this.province !== "MB") break;
        if (this.data.VehicleOwnedLeasedCd === "O") {
          this.clearAndDisable(covName);
        }
        if (checked) {
          if (["PPV"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("TP");
            this.checkCoverage("AUVLU");
          }
        }
        break;
      case "EEF43":
        if (this.transactionType === "QQ" && this.province === "SK") {
          const currentYear = moment().year();
          const vehicleYear = parseInt(this.data.VehiclePurchaseDateDt, 10);
          if (currentYear - vehicleYear > 5) {
            this.clearAndDisable(covName);
          }
        }
        break;
      case "EEF5":
        if (["PPV","ATV","MOT","MOL","MOU","SNO","SNL","SNU","VST","PPT"].includes(this.data.VehicleTypeCd) && this.data.VehicleOwnedLeasedCd === "L") {
          this.checkAndDisable(covName);
        }
        break;
      case "CMP":
        if (!checked) {
          if (["PPV","TRA"].includes(this.policyCode)) {
            this.uncheckCoverage("COL");
          }
          if (["PPT"].includes(this.policyCode) && this.province === "SK") {
            this.uncheckCoverage("SP");
            this.uncheckCoverage("COL");
          }
          if (["PPV"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("AUDEV");
            this.uncheckCoverage("SEF20");
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("COL");
            this.uncheckCoverage("AUVLU");
            this.uncheckCoverage("AUDEV");
            this.uncheckCoverage("SEF19A");
          }
        } else {
          if (["PPV","MOL","MOU","ACS"].includes(this.policyCode)) {
            this.uncheckCoverage("SP");
          }
          if (["PPV","MOL"].includes(this.policyCode)) {
            this.checkCoverage("COL");
          }
          if (["TRA"].includes(this.policyCode)) {
            this.checkCoverage("COL");
            if (this.data["VehicleTRATypeOfUseCd"] == "TC") this.checkCoverage("AUVLU");
          }
          if (["PPT"].includes(this.policyCode) && (this.isVehicleLicensed() || this.province === "SK")) {
            this.checkCoverage("COL");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB" && this.getCoverage("COL")?.get("CoverageSelectedInd").value) {
            this.checkCoverage("AUVLU");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("COL");
            this.checkCoverage("AUVLU");
            if (!this.getCoverage("SEF19A")?.get("CoverageSelectedInd").value) this.checkCoverage("AUDEV");
          }
        }
        break;
      case "COL":
        if (!checked) {
          if (["PPV","MOL","TRA"].includes(this.policyCode)) {
            this.uncheckCoverage("CMP");
          }
          if (["PPT"].includes(this.policyCode) && this.province === "SK") {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("SP");
          }
          if (["PPV"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("AUDEV");
            this.uncheckCoverage("SEF20");
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
          if (["PPT"].includes(this.policyCode) && this.isVehicleLicensed()) {
            this.uncheckCoverage("CMP");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("AUVLU");
            this.uncheckCoverage("AUDEV");
            this.uncheckCoverage("SEF19A");
          }
        } else {
          if (["PPV","PPT"].includes(this.policyCode)) {
            this.checkCoverage("CMP");
          }
          if (["TRA"].includes(this.policyCode)) {
            this.checkCoverage("CMP");
            if (this.data["VehicleTRATypeOfUseCd"] == "TC") this.checkCoverage("AUVLU");
          }
          if (["VST"].includes(this.policyCode) && this.province === "MB" && this.getCoverage("CMP")?.get("CoverageSelectedInd").value) {
            this.checkCoverage("AUVLU");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("AUVLU");
            if (!this.getCoverage("SEF19A")?.get("CoverageSelectedInd").value) this.checkCoverage("AUDEV");
          }
        }
        break;
      case "TP":
        if (!checked) {
          if (["VST"].includes(this.policyCode) && this.province === "MB") {
            this.uncheckCoverage("AUVPL");
            this.uncheckCoverage("AUVPS");
          }
        }
        break;
      case "SP":
        if (!checked) break;
        if (["PPV","MOL","MOU","ACS"].includes(this.policyCode)) {
          this.uncheckCoverage("CMP");
        }
        if (["PPT"].includes(this.policyCode) && this.province === "SK") {
          this.checkCoverage("COL");
        }
        break;
      case "AUDEV":
        if (!checked) {
          if (["ACS"].includes(this.policyCode) && this.province === "MB" && !this.getCoverage("SEF19A")?.get("CoverageSelectedInd").value) {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("COL");
            this.uncheckCoverage("AUVLU");
          }
        } else {
          if (["PPV"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
          }
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("AUVLU");
            this.uncheckCoverage("SEF19A");
          }
        }
        break;
      case "SEF20":
        if (checked && ["PPV"].includes(this.policyCode) && this.province === "MB") {
          this.checkCoverage("CMP");
          this.checkCoverage("COL");
        }
        break;
      case "SEF19A":
        if (!checked) {
          if (["ACS"].includes(this.policyCode) && this.province === "MB" && !this.getCoverage("AUDEV")?.get("CoverageSelectedInd").value) {
            this.uncheckCoverage("CMP");
            this.uncheckCoverage("COL");
            this.uncheckCoverage("AUVLU");
          }
        } else {
          if (["ACS"].includes(this.policyCode) && this.province === "MB") {
            this.checkCoverage("CMP");
            this.checkCoverage("COL");
            this.checkCoverage("AUVLU");
            this.uncheckCoverage("AUDEV");
          }
        }
        break;
    }
  }

  /**
   * Called whenever a coverage is toggled. When the coverage given in the parameters 
   * has a value changed by the user, it can run special logic here
   * @param row the row that the coverage belongs to
   * @param covName the coverage name
   * @param value the value of the subscribed field
   */
  handleSpecialLogic(row: FormGroup, covName: any, value: any): void {
    if (this.policyCode == "SEAR") {
      if (covName == "BUILA" || covName == "PPAR") {
        if (!value) return;
        this.toggleCoverage("BUILF", false);
        this.toggleCoverage("PPFEC", false);
        if (covName == "PPAR") this.toggleCoverage("BUILA", true);
      } else if (covName == "BUILF" || covName == "PPFEC") {
        if (!value) return;
        this.toggleCoverage("BUILA", false);
        this.toggleCoverage("PPAR", false);
        if (covName == "PPFEC") this.toggleCoverage("BUILF", true);
      }
    }

    // AUTO Logic
    if (this.policyCode == "ACS") {
      if (["AUDEV","SEF19A"].includes(covName)) {
        if (this.getCoverage("AUDEV").get("CoverageSelectedInd").value && this.getCoverage("SEF19A").get("CoverageSelectedInd").value) {
          if (covName == "AUDEV") this.toggleCoverage("SEF19A", false);
          else this.toggleCoverage("AUDEV", false);
        }
      }
    }
  }

  /**
   * Called whenever a coverage is toggled. When the coverage given in the parameters 
   * has its limit changed by the user, it sets the limits of coverages dependent on it to their
   * new appropriate value.
   * @param row the row that the coverage belongs to
   * @param covName the coverage name
   */
  handleLimitSubscriptionDefaults(row: FormGroup, covName: string, overwrite: boolean = true) {
    const limit = this.getLimit(covName);
    const deduct = this.getDeductible(covName);
    if (this.policyCode == "HO") {
      switch (covName) {
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("BEDBRK"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ANLE"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("E33DYC"), limit, undefined, 2000000);
          this.setLimitDefault(overwrite, this.getCoverage("E40HBL"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("WALIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("E14LAO"), limit);
          break;
        case "COVA":
          this.setLimitDefault(overwrite, this.getCoverage("COVB"), limit * 0.15, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("COVC"), limit * 0.80, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 0.25, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("E93"), limit);
          this.setDeductibleDefault(this.getCoverage("COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E9LSBU"), deduct);
          this.setDeductibleDefault(this.getCoverage("E29BYL"), deduct);
          this.setDeductibleDefault(this.getCoverage("E13LA1"), deduct);
          this.setDeductibleDefault(this.getCoverage("E43DAC"), deduct);
        // fall through
        case "COVB":
        case "COVC":
        case "COVD":
          const COVA = this.getLimit("COVA");
          const COVB = this.getLimit("COVB");
          const COVC = this.getLimit("COVC");
          const COVD = this.getLimit("COVD");
          this.setLimitDefault(overwrite, this.getCoverage("E27CSL"), COVA + COVB + COVC + COVD);
          this.setLimitDefault(overwrite, this.getCoverage("E13LA1"), COVC * 5);
          break;
      }
    } else if (this.policyCode == "CO") {
      switch (covName) {
        case "COVC":
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 2);
          this.setLimitDefault(overwrite, this.getCoverage("E12CCP"), limit * 5);
          this.setLimitDefault(overwrite, this.getCoverage("E13LA1"), limit * 5);
          this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit * 4);
          this.setLimitDefault(overwrite, this.getCoverage("E93C"), limit);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E12CCP"), deduct);
          this.setDeductibleDefault(this.getCoverage("E13LA1"), deduct);
          this.setDeductibleDefault(this.getCoverage("E16IAB"), deduct);
          this.setDeductibleDefault(this.getCoverage("E43DAC"), deduct);
          this.setDeductibleDefault(this.getCoverage("E28SLE"), deduct);
          this.setDeductibleDefault(this.getCoverage("E39HBP"), deduct);
          this.setDeductibleDefault(this.getCoverage("E5RENT"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILBUSP"), deduct);
          break;
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("E14LAO"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("BEDBRK"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ANLE"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("E33DYC"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("E40HBL"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("RESEMP"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("WALIA"), limit);
          break;
      }
    } else if (["COR", "COS"].includes(this.policyCode)) {
      switch (covName) {
        case "COVC":
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 2);
          this.setLimitDefault(overwrite, this.getCoverage("E12CCP"), limit * 5);
          this.setLimitDefault(overwrite, this.getCoverage("E13LA1"), limit * 5);
          this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit * 4);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E12CCP"), deduct);
          this.setDeductibleDefault(this.getCoverage("E13LA1"), deduct);
          this.setDeductibleDefault(this.getCoverage("E16IAB"), deduct);
          this.setDeductibleDefault(this.getCoverage("E43DAC"), deduct);
          this.setDeductibleDefault(this.getCoverage("E5RENT"), deduct);
          break;
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("E14LAO"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit);
          break;
      }
    } else if (this.policyCode == "CPL") {
      switch (covName) {
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("ANLE"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("BEDBRK"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("E14LAO"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("E33DYC"), limit, undefined, 2000000);
          this.setLimitDefault(overwrite, this.getCoverage("E40HBL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("INCBUS"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("WALIA"), limit)
          break;
      }
    } else if (this.policyCode == "CGL") {
      switch (covName) {
        case "CGLABI":
          this.setLimitDefault(overwrite, this.getCoverage("CGLBPI"), limit);
          break;
      }
    } else if (this.policyCode == "SEAH") {
      switch (covName) {
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("WALIA"), limit);
          break;
        case "COVA":
          this.setLimitDefault(overwrite, this.getCoverage("COVB"), limit * 0.10, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("COVC"), limit * 0.50, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 0.25, 0, 99999999);
          this.setDeductibleDefault(this.getCoverage("COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E29BYL"), deduct);
        // fall through
        case "COVB":
        case "COVC":
        case "COVD":
          const COVA = this.getLimit("COVA");
          const COVB = this.getLimit("COVB");
          const COVC = this.getLimit("COVC");
          const COVD = this.getLimit("COVD");
          this.setLimitDefault(overwrite, this.getCoverage("E27CSL"), COVA + COVB + COVC + COVD, 0, 99999999);
          break;
      }
    } else if (this.policyCode == "FEC") {
      switch (covName) {
        case "BUILF":
          if (this.getLimit("BUILA") == 0) {
            this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit, 0, 99999999);
          }
          break;
        case "BUILA":
          this.setLimitDefault(overwrite, this.getCoverage("PPAR"), limit * 0.10, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit, 0, 99999999);
          break;
      }
      switch (covName) {
        case "PPAR":
        case "BUILA":
        case "PPFEC":
          this.setLimitDefault(overwrite, this.getCoverage("E93C"), limit);
        case "BUILF":
          const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), fec_deduct.deductible);
          this.setLimitDefault(true, this.getCoverage("E13LA1"), this.getLimit("PPFEC") * 5);
          this.setDeductibleDefault(this.getCoverage("E13LA1"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E16IAB"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E43DAC"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E5RENT"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("RIDFEC"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E29BYL"), fec_deduct.deductible);
          this.setLimitDefault(overwrite, this.getCoverage("E93"), limit);
          break;
      }
    } else if (this.policyCode == "GDC") {
      switch (covName) {
        case "PPAR":
          this.setLimitDefault(overwrite, this.getCoverage("E93C"), limit);
        case "BUILA":
          if (covName == "BUILA") {
            // ARC limit is 10% of ARB and ARC deductible always follows ARB
            this.setLimitDefault(overwrite, this.getCoverage("PPAR"), limit * 0.10, 0, 99999999);
            this.syncDeductible(this.getCoverage("PPAR"), deduct);
          }
          const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
          this.setLimitDefault(true, this.getCoverage("E13LA1"), this.getLimit("PPAR") * 5);
          this.setDeductibleDefault(this.getCoverage("E13LA1"), fec_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E43DAC"), fec_deduct.deductible);
          this.setLimitDefault(overwrite, this.getCoverage("E93"), limit);
          break;
      }
    } else if (this.policyCode == "PINS") {
      switch (covName) {
        case "PINSF":
        case "PINSA":
          const PINSA = this.getLimit("PINSA");
          const PINSF = this.getLimit("PINSF");
          this.setLimitDefault(overwrite, this.getCoverage("E18BRG"), PINSF + PINSA);

          const pins_deduct = this.getDeductibleFromPossibleArray(['PINSA', 'PINSF']);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), pins_deduct.deductible);
          this.setDeductibleDefault(this.getCoverage("E18BRG"), pins_deduct.deductible);
          break;
      }
    } else if (this.policyCode == "SEAR") {
      switch (covName) {
        case "BUILA":
        case "BUILF":
          this.setDeductibleDefault(this.getCoverage("PPFEC"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E18BRG"), deduct);
          this.setDeductibleDefault(this.getCoverage("E5RENT"), deduct);
          this.setDeductibleDefault(this.getCoverage("E7VAND"), deduct);
          this.setDeductibleDefault(this.getCoverage("E29BYL"), deduct);
          break;
        case "PPFEC":
          this.setLimitDefault(overwrite, this.getCoverage("E18BRG"), limit);
          break;
      }
    } else if (this.policyCode == "SETP") {
      switch (covName) {
        case "COVC":
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 0.25);
          this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit * 0.10, 0, 99999999);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E16IAB"), deduct);
          this.setDeductibleDefault(this.getCoverage("E28SLE"), deduct);
          break;
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          break;
      }
    } else if (this.policyCode == "TE") {
      switch (covName) {
        case "COVC":
          this.setLimitDefault(overwrite, this.getCoverage("COVD"), limit * 0.25);
          this.setLimitDefault(overwrite, this.getCoverage("E16IAB"), limit * 0.10, 0, 99999999);
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit);
          this.setDeductibleDefault(this.getCoverage("COVD"), deduct);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), deduct);
          this.setDeductibleDefault(this.getCoverage("E16IAB"), deduct);
          this.setDeductibleDefault(this.getCoverage("E28SLE"), deduct);
          this.setDeductibleDefault(this.getCoverage("E39HBP"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILAUTO"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILBOOK"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILBUSP"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILGRAV"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILMEQP"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILNURS"), deduct);
          this.setDeductibleDefault(this.getCoverage("ILSOFT"), deduct);
          this.setDeductibleDefault(this.getCoverage("STUDEX"), deduct);
          break;
        case "COVE":
          this.setLimitDefault(overwrite, this.getCoverage("ADDLRM"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADDRR"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI1"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ADNILI2"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("ANLE"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("BEDBRK"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("E33DYC"), limit, undefined, 2000000);
          this.setLimitDefault(overwrite, this.getCoverage("E40HBL"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("INCBUS"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("RESEMP"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("SPLIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("WALIA"), limit);
          this.setLimitDefault(overwrite, this.getCoverage("WBLIA"), limit);
          break;
      }
    } else if (this.policyCode == "PD") {
      switch (covName) {
        case "PD-COMP-COVA":
        case "PD-EP-COVA":
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVB`), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVC`), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVD`), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-A"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-C"), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVB`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVC`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVD`), deduct);
          break;
        case "EQ-A":
          this.setDeductibleDefault(this.getCoverage("EQ-A"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          break;
      }
    } else if (this.policyCode == "SNS") {
      switch (covName) {
        case "SNS-COMP-COVA":
        case "SNS-EP-COVA":
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVB`), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVC`), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVD`), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-A"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-C"), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVB`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVC`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVD`), deduct);
          break;
        case "EQ-A":
          this.setDeductibleDefault(this.getCoverage("EQ-A"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          break;
      }
    } else if (this.policyCode == "SD") {
      switch (covName) {
        case "SD-COMP-COVA":
        case "SD-BP-COVA":
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVB`), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVC`), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVD`), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-A"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-C"), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVB`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVC`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVD`), deduct);
          break;
        case "EQ-A":
          this.setDeductibleDefault(this.getCoverage("EQ-A"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          break;
      }
    } else if (this.policyCode == "MH") {
      switch (covName) {
        case "MH-BP-COVA":
        case "MH-COMP-COVA":
        case "MH-LP-COVA":
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVB`), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVC`), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVD`), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-A"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-C"), limit * 0.7)
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVB`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVC`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVD`), deduct);
          break;
        case "EQ-A":
          this.setDeductibleDefault(this.getCoverage("EQ-A"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-LC"), deduct);
          break;
      }
    } else if (this.policyCode == "RD") {
      switch (covName) {
        case "RD-LP-COVA":
        case "RD-BP-COVA":
        case "RD-COMP-COVA":
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVB`), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-COVC`), limit * 0.5)
          this.setLimitDefault(overwrite, this.getCoverage(`${this.packageType}-RI`), limit * 0.5)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-A"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1)
          this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVB`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-COVC`), deduct);
          this.setDeductibleDefault(this.getCoverage(`${this.packageType}-RI`), deduct);
          break;
        case "EQ-A":
          this.setDeductibleDefault(this.getCoverage("EQ-A"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-LC"), deduct);
          break;
      }
    } else if (["FBCP", "FBEP", "FBBP", "FBLP"].includes(this.formTypeCd)) {
      switch (covName) {
        case "FBCP":
        case "FBBP":
        case "FBEP":
        case "FBLP":
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, this.getCoverage("WATERPROL"), limit)
            this.setDeductibleDefault(this.getCoverage("WATERPROL"), deduct);
          }
      }
    } else if (this.formTypeCd == "GBCP") {
      switch (covName) {
        case "CP-GBS":
          this.setLimitDefault(overwrite, this.getCoverage("HAIL"), limit)
          break;
      }
    } else if (this.policyCode === "") { // MFI coverages
      switch (covName) {
        case "EAO":
          this.setLimitDefault(overwrite, this.getCoverage("AEP"), (limit + 1000000));
          break;
        case "EQ-A":
          this.setLimitDefault(overwrite, this.getCoverage("EQ-B"), limit * 0.1);
          this.setLimitDefault(overwrite, this.getCoverage("EQ-C"), limit * 0.7);
          this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          this.setDeductibleDefault(this.getCoverage("EQ-LC"), deduct);
          break;
        case "EQ-C":
          if (this.getCoverage("EQ-A") == null) {
            this.setLimitDefault(overwrite, this.getCoverage("EQ-D"), limit * 0.2);
            this.setDeductibleDefault(this.getCoverage("EQ-D"), deduct);
          }
          break;
        case "PD-COMP-COVA":
          this.setDeductibleDefault(this.getCoverage("PD-COMP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("PD-COMP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("PD-COMP-COVD"), deduct);
          break;
        case "TE-EP-COVC":
          this.setDeductibleDefault(this.getCoverage("TE-EP-COVD"), deduct);
          break;
        case "TE-COMP-COVC":
          this.setDeductibleDefault(this.getCoverage("TE-COMP-COVD"), deduct);
          break;
        case "SNS-COMP-COVA":
          this.setDeductibleDefault(this.getCoverage("SNS-COMP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-COMP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-COMP-COVD"), deduct);
          break;
        case "SNS-EP-COVA":
          this.setDeductibleDefault(this.getCoverage("SNS-EP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-EP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-EP-COVD"), deduct);
          break;
        case "SNS-LP-COVA":
          this.setDeductibleDefault(this.getCoverage("SNS-LP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-LP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("SNS-LP-COVD"), deduct);
          break;
        case "SD-LP-COVA":
          this.setDeductibleDefault(this.getCoverage("SD-LP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("SD-LP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("SD-LP-COVD"), deduct);
          break;
        case "SD-EP-COVA":
          this.setDeductibleDefault(this.getCoverage("SD-EP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("SD-EP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("SD-EP-COVD"), deduct);
          break;
        case "RD-EP-COVA":
          this.setDeductibleDefault(this.getCoverage("RD-EP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("RD-EP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("RD-EP-COVD"), deduct);
          break;
        case "RD-COMP-COVA":
          this.setDeductibleDefault(this.getCoverage("RD-COMP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("RD-COMP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("RD-COMP-COVD"), deduct);
          break;
        case "MH-LP-COVA":
          this.setDeductibleDefault(this.getCoverage("MH-LP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-LP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-LP-COVD"), deduct);
          break;
        case "MH-EP-COVA":
          this.setDeductibleDefault(this.getCoverage("MH-EP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-EP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-EP-COVD"), deduct);
          break;
        case "MH-COMP-COVA":
          this.setDeductibleDefault(this.getCoverage("MH-COMP-COVB"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-COMP-COVC"), deduct);
          this.setDeductibleDefault(this.getCoverage("MH-COMP-COVD"), deduct);
          break;
      }
    }

    // AUTO COVERAGES
    // Handling this by coverage type instead of by vehicle type since I think it should be easier
    if (covName == "TP") {
      if (["PPV","ACS"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, this.getCoverage("SEF44"), limit, 1000000, 2000000);
        this.setLimitDefault(overwrite, this.getCoverage("EEF44"), limit, 1000000, 2000000);
        this.setLimitDefault(overwrite, this.getCoverage("AB"), limit, 1000000, 2000000);
      }
    } else if (covName == "CMP") {
      if (["ACS", "ATV"].includes(this.policyCode)) {
        this.setDeductibleDefault(this.getCoverage("RHG"), deduct, 100, 1000);
      } else if (["MOT","MOL","MOU","VST"].includes(this.policyCode)) {
        this.setDeductibleDefault(this.getCoverage("RHG"), deduct, 100, 700);
      } else if (["SNO","SNL","SNU"].includes(this.policyCode)) {
        this.setDeductibleDefault(this.getCoverage("RHG"), deduct, 200, 700);
      } else if (["PPT"].includes(this.policyCode)) {
        this.setDeductibleDefault(this.getCoverage("RHG"), deduct, 50, 350);
      } else if (this.policyCode == "PPV") {
        if (this.getCoverage("COL")?.get("CoverageSelectedInd").value && this.getCoverage("CMP")?.get("CoverageSelectedInd").value) {
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").setValue(true);
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").disable();
        } else {
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").enable();
        }
      }

      if (this.transactionType === "QQ" && this.province === "SK" && !["PPV","PPT"].includes(this.policyCode)) {
        if (row.get("CoverageSelectedInd").value) {
          this.clearAndDisable("SP");
        } else {
          this.getCoverage("SP")?.get("CoverageSelectedInd").enable({ emitEvent: false })
        }
      }
    } else if (covName == "COL") {
      if (this.policyCode == "PPV") {
        if (this.getCoverage("COL")?.get("CoverageSelectedInd").value && this.getCoverage("CMP")?.get("CoverageSelectedInd").value) {
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").setValue(true);
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").disable();
        } else {
          this.getCoverage("AUVLU")?.get("CoverageSelectedInd").enable();
        }
      }
    } else if (covName == "SP") {
      if (this.transactionType === "QQ" && this.province === "SK" && !["PPV","PPT"].includes(this.policyCode)) {
          if (row.get("CoverageSelectedInd").value) {
            this.clearAndDisable("CMP");
          } else {
            this.getCoverage("CMP")?.get("CoverageSelectedInd").enable({ emitEvent: false })
          }
      }
    } else if (covName == "E93") {
      this.getCoverage("E93C")?.get("CoverageDeductibleAmt").setValue(row.get("CoverageDeductibleAmt").value);
    } else if (covName == "E93C") {
      this.getCoverage("E93")?.get("CoverageDeductibleAmt").setValue(row.get("CoverageDeductibleAmt").value);
    }
  }

  /**
   * Sets default values for a row when it is enabled by the user.
   * i.e. if user clicks the checkbox, this function is called.
   * @param row the row that was enabled
   */
  handleSpecialDefaults(row: FormGroup, overwrite: boolean = true) {
    if (overwrite === false) {
      return;
    }
    const covName = row.get("CoverageNameCd").value;
    if (this.policyCode == "HO") {
      switch (covName) {
        case "E27CSL":
          const COVA = this.getLimit("COVA");
          const COVB = this.getLimit("COVB");
          const COVC = this.getLimit("COVC");
          const COVD = this.getLimit("COVD");
          this.setLimitDefault(overwrite, row, COVA + COVB + COVC + COVD);
          break;
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "ADDRR":
        case "ADDLRM":
        case "BEDBRK":
        case "ANLE":
        case "E40HBL":
        case "SPLIA":        
        case "WALIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
        case "E9LSBU":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E33DYC":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"), undefined, 2000000);
          break;
        case "E9LSBU":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E29BYL":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E13LA1":
          this.setLimitDefault(true, row, this.getLimit("COVC") * 5);
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E14LAO":
          this.setLimitDefault(true, row, this.getLimit("COVE"));
          break;
        case "E43DAC":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E93":
          this.setLimitDefault(true, row, this.getLimit("COVA"));
          break;
      }
    } else if (this.policyCode == "CO") {
      switch (covName) {
        case "COVD":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 2);
          break;
        case "E12CCP":
        case "E13LA1":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 5);
          break;
        case "E16IAB":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 4);
          break;
        case "E14LAO":
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "ADDRR":
        case "ADDLRM":
        case "BEDBRK":
        case "ANLE":
        case "E33DYC":
        case "E40HBL":
        case "RESEMP":
        case "SPLIA":
        case "WALIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
        case "E93C":
          this.setLimitDefault(true, row, this.getLimit("COVC"));
          break;
      }
      switch (covName) {
        case "E10SWR":
        case "E12CCP":
        case "E13LA1":
        case "E16IAB":
        case "E43DAC":
        case "E28SLE":
        case "E39HBP":
        case "E5RENT":
        case "ILBUSP":
          this.setDeductibleDefault(row, this.getDeductible("COVC"));
          break;
      }
    } else if (["COR", "COS"].includes(this.policyCode)) {
      switch (covName) {
        case "COVD":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 2);
          break;
        case "E12CCP":
        case "E13LA1":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 5);
          break;
        case "E16IAB":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 4);
          break;
        case "E14LAO":
        case "ADDLRM":
        case "ADDRR":
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "SPLIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
      }
      switch (covName) {
        case "E10SWR":
        case "E12CCP":
        case "E13LA1":
        case "E16IAB":
        case "E43DAC":
        case "E5RENT":
          this.setDeductibleDefault(row, this.getDeductible("COVC"));
          break;
      }
    } else if (this.policyCode == "CPL") {
      switch (covName) {
        case "ADDLRM":
        case "ADDRR":
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "ANLE":
        case "BEDBRK":
        case "E14LAO":
        case "E40HBL":
        case "INCBUS":
        case "SPLIA":
        case "WALIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
        case "E33DYC":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"), undefined, 2000000);
          break;
      }
    } else if (this.policyCode == "CGL") {
      switch (covName) {
        case "CGLBPI":
          this.setLimitDefault(overwrite, row, this.getLimit("CGLABI"));
      }
    } else if (this.policyCode == "SEAH") {
      switch (covName) {
        case "COVB":
          this.setLimitDefault(overwrite, row, this.getLimit("COVA") * 0.10, 0, 99999999);
          break;
        case "COVC":
          this.setLimitDefault(overwrite, row, this.getLimit("COVA") * 0.50, 0, 99999999);
          break;
        case "COVD":
          this.setLimitDefault(overwrite, row, this.getLimit("COVA") * 0.25, 0, 99999999);
          break;
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "SPLIA":
        case "WALIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
        case "E27CSL":
          const COVA = this.getLimit("COVA");
          const COVB = this.getLimit("COVB");
          const COVC = this.getLimit("COVC");
          const COVD = this.getLimit("COVD");
          this.setLimitDefault(overwrite, row, COVA + COVB + COVC + COVD, 0, 99999999);
          break;
        case "E10SWR":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
        case "E29BYL":
          this.setDeductibleDefault(row, this.getDeductible("COVA"));
          break;
      }
    } else if (this.policyCode == "FEC") {
      switch (covName) {
        case "E16IAB":
          if (this.getLimit("BUILA") == 0) this.setLimitDefault(overwrite, row, this.getLimit("BUILF"), 0, 99999999);
          else this.setLimitDefault(overwrite, row, this.getLimit("BUILA"), 0, 99999999);
          break;
        case "E93C":
          this.setLimitDefault(true, row, this.getLimit("PPFEC"));
          break;
        case "E93":
          this.setLimitDefault(true, row, this.getLimit("BUILF"));
          break;
      }
      switch (covName) {
        case "E10SWR":
        case "E13LA1":
          this.setLimitDefault(true, row, this.getLimit("PPFEC") * 5);
          // fall through
        case "E16IAB":
        case "E29BYL":
        case "E43DAC":
        case "E5RENT":
        case "RIDFEC":
          const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
          this.setDeductibleDefault(row, fec_deduct.deductible);
          break;
      }
    } else if (this.policyCode == "PINS") {
      switch (covName) {
        case "E18BRG":
          this.setLimitDefault(overwrite, row, this.getLimit("PINSF") + this.getLimit("PINSA"));
        // fall through
        case "E10SWR":
          const pins_deduct = this.getDeductibleFromPossibleArray(['PINSA', 'PINSF']);
          this.setDeductibleDefault(this.getCoverage("E10SWR"), pins_deduct.deductible);
          break;
      }
    } else if (this.policyCode == "SEAR") {
      switch (covName) {
        case "E18BRG":
          this.setLimitDefault(overwrite, row, this.getLimit("PPFEC"));
        // Fall through
        case "PPFEC":
        case "E10SWR":
        case "E5RENT":
        case "E7VAND":
          this.setDeductibleDefault(row, this.getDeductible("BUILF"));
          break;
        case "BUILA":
          this.setLimitDefault(overwrite, row, this.data["RiskReplacementCostAmt"], 25000, 99999999);
          break;
        case "BUILF":
          this.setLimitDefault(overwrite, row, this.data["RiskReplacementCostAmt"], 0, 99999999);
          break;
        case "E29BYL":
          const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
          this.setDeductibleDefault(row, fec_deduct.deductible);
          break;
      }
    } else if (this.policyCode == "GDC") {
      switch (covName) {
        case "BUILA":
          this.setLimitDefault(overwrite, row, this.data["RiskReplacementCostAmt"], 200000, 99999999);
          this.setDeductibleDefault(this.getCoverage("E29BYL"), this.getDeductible("BUILA"));
          break;
        case "PPAR":
          this.setLimitDefault(overwrite, row, this.getLimit("BUILA") * 0.10, 0, 99999999);
          this.syncDeductible(row, this.getDeductible("BUILA"));
          break;
        case "E13LA1":
          this.setLimitDefault(true, row, this.getLimit("PPAR") * 5);
          this.setDeductibleDefault(row, this.getDeductible("PPAR"));
          break;
        case "E29BYL":
          const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
          this.setDeductibleDefault(row, fec_deduct.deductible);
          break;
        case "E43DAC":
          const deductible = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA']);
          this.setDeductibleDefault(row, deductible.deductible);
          break;
        case "E93C":
          this.setLimitDefault(true, row, this.getLimit("PPAR"));
          break;
        case "E93":
          this.setLimitDefault(true, row, this.getLimit("BUILA"));
          break;
      }
    } else if (this.policyCode == "SETP") {
      switch (covName) {
        case "COVD":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 0.25);
          break;
        case "E16IAB":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 0.10, this.getLimit("COVC") * 0.10, 99999999);
          break;
        case "ADDLRM":
        case "ADDRR":
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
      }
      switch (covName) {
        case "E10SWR":
          this.setDeductibleDefault(row, this.getDeductible("COVC"));
          break;
        case "E16IAB":
        case "E28SLE":
          this.setDeductibleDefault(row, this.getDeductible("BUILF"));
          break;
      }
    } else if (this.policyCode == "TE") {
      switch (covName) {
        case "COVD":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 0.25);
          break;
        case "E16IAB":
          this.setLimitDefault(overwrite, row, this.getLimit("COVC") * 0.10, this.getLimit("COVC") * 0.10, 99999999);
          break;
        case "ADDLRM":
        case "ADDRR":
        case "ADNILI":
        case "ADNILI1":
        case "ADNILI2":
        case "ANLE":
        case "BEDBRK":
        case "E40HBL":
        case "INCBUS":
        case "RESEMP":
        case "SPLIA":
        case "WALIA":
        case "WBLIA":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"));
          break;
        case "E33DYC":
          this.setLimitDefault(overwrite, row, this.getLimit("COVE"), undefined, 2000000);
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVC`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVC`));
          }
          break;
      }
      switch (covName) {
        case "E10SWR":
        case "E16IAB":
        case "E28SLE":
        case "E39HBP":
        case "ILAUTO":
        case "ILBOOK":
        case "ILBUSP":
        case "ILGRAV":
        case "ILMEQP":
        case "ILNURS":
        case "ILSOFT":
        case "STUDEX":
          this.setDeductibleDefault(row, this.getDeductible("COVC"));
      }
    } else if (this.policyCode == "PD") {
      switch (covName) {
        case "E10SWR":
        case "E16IAB":
        case "E28SLE":
        case "E39HBP":
        case "ILAUTO":
        case "ILBOOK":
        case "ILBUSP":
        case "ILGRAV":
        case "ILMEQP":
        case "ILNURS":
        case "ILSOFT":
        case "STUDEX":
          this.setDeductibleDefault(row, this.getDeductible("COVC"));
          break;
        case "EQ-A":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          this.setDeductibleDefault(this.getCoverage("EQ-A"), this.getDeductible(covName));
          break;
        case "EQ-B":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.1);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), this.getDeductible("EQ-A"));
          break;
        case "EQ-C":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.7);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), this.getDeductible("EQ-A"));
          break;
        case "EQ-D":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.2);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), this.getDeductible("EQ-A"));
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVA`));
          }
          break;
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          break;
      }
    } else if (this.policyCode == "SNS") {
      switch (covName) {
        case "EQ-A":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          this.setDeductibleDefault(this.getCoverage("EQ-A"), this.getDeductible(covName));
          break;
        case "EQ-B":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.1);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), this.getDeductible("EQ-A"));
          break;
        case "EQ-C":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.7);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), this.getDeductible("EQ-A"));
          break;
        case "EQ-D":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.2);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), this.getDeductible("EQ-A"));
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVA`));
          }
          break;
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          break;
      }
    } else if (this.policyCode == "SD") {
      switch (covName) {
        case "EQ-A":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          this.setDeductibleDefault(this.getCoverage("EQ-A"), this.getDeductible(covName));
          break;
        case "EQ-B":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.1);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), this.getDeductible("EQ-A"));
          break;
        case "EQ-C":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.7);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), this.getDeductible("EQ-A"));
          break;
        case "EQ-D":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.2);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), this.getDeductible("EQ-A"));
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVA`));
          }
          break;
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          break;
      }
    } else if (this.policyCode == "MH") {
      switch (covName) {
        case "EQ-A":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          this.setDeductibleDefault(this.getCoverage("EQ-A"), this.getDeductible(covName));
          break;
        case "EQ-B":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.1);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), this.getDeductible("EQ-A"));
          break;
        case "EQ-C":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.7);
          this.setDeductibleDefault(this.getCoverage("EQ-C"), this.getDeductible("EQ-A"));
          break;
        case "EQ-D":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.2);
          this.setDeductibleDefault(this.getCoverage("EQ-D"), this.getDeductible("EQ-A"));
          break;
        case "EQ-LC":
          this.setDeductibleDefault(this.getCoverage("EQ-LC"), this.getDeductible("EQ-A"));
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVA`));
          }
          break;
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          break;
      }
    } else if (this.policyCode == "RD") {
      switch (covName) {
        case "EQ-A":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          this.setDeductibleDefault(this.getCoverage("EQ-A"), this.getDeductible(covName));
          break;
        case "EQ-B":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`) * 0.1);
          this.setDeductibleDefault(this.getCoverage("EQ-B"), this.getDeductible("EQ-A"));
          break;
        case "EQ-LC":
          this.setDeductibleDefault(this.getCoverage("EQ-LC"), this.getDeductible("EQ-A"));
          break;
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
            this.setDeductibleDefault(row, this.getDeductible(`${this.packageType}-COVA`));
          }
          break;
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(`${this.packageType}-COVA`));
          break;
      }
    } else if (["FBCP", "FBEP", "FBBP", "FBLP"].includes(this.formTypeCd)) {
      switch (covName) {
        case "WATERPROL":
          if (this.province != 'MB') {
            this.setLimitDefault(overwrite, row, this.getLimit(this.formTypeCd));
            this.setDeductibleDefault(row, this.getDeductible(this.formTypeCd));
            break;
          }
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit(this.formTypeCd));
          break;
      }
    } else if (this.formTypeCd == "GBCP") {
      switch (covName) {
        case "HAIL":
          this.setLimitDefault(overwrite, row, this.getLimit("CP-GBS"));
          break;
      }
    } else if (this.policyCode == "PPV") { // AUTO coverages
      switch (covName) {
        case "AUDEV":
          this.setLimitDefault(overwrite, row, this.data.VehicleStatedValueNum);
          break;
      }
    } else if (this.policyCode == "") {
      switch (covName) {
        case "EQ-A":
          const possibleFields_eqa = [
            "PD-COMP-COVA",
            "TE-EP-COVC",
            "TE-COMP-COVC",
            "SNS-COMP-COVA",
            "SNS-EP-COVA",
            "SNS-LP-COVA",
            "SD-LP-COVA",
            "SD-EP-COVA",
            "RD-EP-COVA",
            "RD-COMP-COVA",
            "MH-LP-COVA",
            "MH-EP-COVA",
            "MH-COMP-COVA",
            "FBCP"
          ];
          const eqaLimitResult = this.getLimitFromPossibleArray(possibleFields_eqa);
          if (!row.get('CoverageLimitAmt'))
            this.setLimitDefault(overwrite, row, eqaLimitResult.limitDefault);

          let eqaDeductDefault: number;
          if (eqaLimitResult.foundField == "FBCP") {
            eqaDeductDefault = 5;
          } else {
            eqaDeductDefault = 10;
          }
          this.setDeductibleDefault(row, eqaDeductDefault);
          break;
        case "EQ-B":
          const possibleParentFields_eqb = [
            "EQ-A",
            "PD-COMP-COVA",
            "TE-EP-COVC",
            "TE-COMP-COVC",
            "SNS-COMP-COVA",
            "SNS-EP-COVA",
            "SNS-LP-COVA",
            "SD-LP-COVA",
            "SD-EP-COVA",
            "RD-EP-COVA",
            "RD-COMP-COVA",
            "MH-LP-COVA",
            "MH-EP-COVA",
            "MH-COMP-COVA",
            "FBCP"
          ];
          const eqbLimitResult = this.getLimitFromPossibleArray(possibleParentFields_eqb);
          if (!row.get('CoverageLimitAmt'))
            this.setLimitDefault(overwrite, row, eqbLimitResult.limitDefault * 0.1);

          let eqbDeductibleDefault = this.getDeductible("EQ-A");
          if (eqbDeductibleDefault == 0) {
            eqbDeductibleDefault = 10;
          }
          this.setDeductibleDefault(row, eqbDeductibleDefault);
          break;
        case "EQ-C":
          const possibleParentFields_eqc = [
            "FCCP",
            "EQ-A",
            "PD-COMP-COVA",
            "TE-EP-COVC",
            "TE-COMP-COVC",
            "SNS-COMP-COVA",
            "SNS-EP-COVA",
            "SNS-LP-COVA",
            "SD-LP-COVA",
            "SD-EP-COVA",
            "RD-EP-COVA",
            "RD-COMP-COVA",
            "MH-LP-COVA",
            "MH-EP-COVA",
            "MH-COMP-COVA"
          ];
          const eqcLimitResult = this.getLimitFromPossibleArray(possibleParentFields_eqc);
          if (!(eqcLimitResult.foundField == "FCCP" ||
            eqcLimitResult.foundField == "TE-EP-COVC" ||
            eqcLimitResult.foundField == "TE-COMP-COVC")) {
            eqcLimitResult.limitDefault = eqcLimitResult.limitDefault * 0.7;
          }
          if (!row.get('CoverageLimitAmt'))
            this.setLimitDefault(overwrite, row, eqcLimitResult.limitDefault);

          let eqcDeductibleDefault = this.getDeductible("EQ-A");
          if (eqcDeductibleDefault == 0) {
            if (eqcLimitResult.foundField == "FCCP") {
              eqcDeductibleDefault = 5;
            } else {
              eqcDeductibleDefault = 10;
            }
          }
          this.setDeductibleDefault(row, eqcDeductibleDefault);
          break;
        case "EQ-D":
          const possibleParentFields_eqd = [
            "EQ-A",
            "PD-COMP-COVA",
            "TE-EP-COVC",
            "TE-COMP-COVC",
            "SNS-COMP-COVA",
            "SNS-EP-COVA",
            "SNS-LP-COVA",
            "SD-LP-COVA",
            "SD-EP-COVA",
            "RD-EP-COVA",
            "RD-COMP-COVA",
            "MH-LP-COVA",
            "MH-EP-COVA",
            "MH-COMP-COVA",
            "FBCP"
          ];
          const eqdLimitResult = this.getLimitFromPossibleArray(possibleParentFields_eqd);
          eqdLimitResult.limitDefault = eqdLimitResult.limitDefault * 0.2;
          if (!row.get('CoverageLimitAmt'))
            this.setLimitDefault(overwrite, row, eqdLimitResult.limitDefault);

          let eqdDeductibleDefault = this.getDeductible("EQ-A");
          if (eqdDeductibleDefault == 0) {
            eqdDeductibleDefault = this.getDeductible("EQ-C");
            if (eqdDeductibleDefault == 0) {
              eqdDeductibleDefault = 10;
            }
          }
          this.setDeductibleDefault(row, eqdDeductibleDefault);
          break;
        case "EQ-LC":
          let eqlcDeductibleDefault = this.getDeductible("EQ-A");
          if (eqlcDeductibleDefault == 0) {
            eqlcDeductibleDefault = 10;
          }
          this.setDeductibleDefault(row, eqlcDeductibleDefault);
          break;
        case "EP-FME":
          if (!row.get('CoverageLimitAmt') && this.additionalDependencies?.['ScheduleItemValueAmt'] != null)
            this.setLimitDefault(overwrite, row, this.additionalDependencies['ScheduleItemValueAmt'].value);
        // Fall through
        case "LUEX":
        case "EBMB":
        case "EP-COVY":
        case "FLHP":
        case "FTL":
        case "HPRO":
        case "PDCC":
        case "PROFLT_ATV":
        case "PROFLT_FTC":
        case "FPSOLAR":
        case "FPDRONE":
          if (this.additionalDependencies?.['ScheduleItemDeductibleCd'] != null)
            this.setDeductibleDefault(row, this.additionalDependencies['ScheduleItemDeductibleCd'].value);
          break;
      }
    }

    // AUTO COVERAGES
    // Handling this by coverage type instead of by vehicle type since I think it should be easier
    if (["EEF19","EEF19A","SEF19A"].includes(covName)) {
      if (["ACS","ATV","MOT","MOL","MOU","SNO","SNL","SNU","VST","PPT"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.data.VehicleStatedValueNum);
      }
    } else if (covName == "EEF43") {
      if (["PPV","VST"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.data.VehiclePurchasePriceNum);
      }
    } else if (covName == "AUVPL") {
      if (["PPV","VST"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.data.VehiclePurchasePriceNum);
      }
    } else if (covName == "AUVPS") {
      if (["PPV","VST"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.data.VehiclePurchasePriceNum);
      }
    } else if (covName == "SEF44") {
      if (["PPV","ACS"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.getLimit("TP") == 1000000 ? 1000000 : 2000000);
      
      }
    } else if (covName == "AUDEV") {
      if (["PPV","TRA","ACS","VST"].includes(this.policyCode)) {
        this.setLimitDefault(overwrite, row, this.data.VehicleStatedValueNum);
      }
    } else if (covName == "RHG") {
      if (["ACS", "ATV"].includes(this.policyCode)) {
        this.setDeductibleDefault(row, this.getDeductible("CMP"), 100, 1000);
      } else if (["MOT","MOL","MOU","VST"].includes(this.policyCode)) {
        this.setDeductibleDefault(row, this.getDeductible("CMP"), 100, 700);
      } else if (["SNO","SNL","SNU"].includes(this.policyCode)) {
        this.setDeductibleDefault(row, this.getDeductible("CMP"), 200, 700);
      } else if (["PPT"].includes(this.policyCode)) {
        this.setDeductibleDefault(row, this.getDeductible("CMP"), 50, 350);
      }
    }
  }

  /**
   * Sets the minimum and maximum allowed limit values for coverages. If the value is outside the allowed limit,
   * an error message is shown to the user.
   */
  handleMinMax() {
    if (this.policyCode == "HO") {
      this.setMinMax("COVA", undefined, 99999999);
      this.setMinMax("COVB", this.getLimit("COVA") * 0.15, 99999999);
      this.setMinMax("COVC", this.getLimit("COVA") * 0.80, 99999999);
      this.setMinMax("COVD", this.getLimit("COVA") * 0.25, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E49ITE", 1, 1000000);
      this.setMinMax("E9LSBU", this.getDeductible("COVA"), undefined, "CoverageDeductibleAmt");
      this.setMinMax("E11CWC", this.getDeductible("COVA"), undefined, "CoverageDeductibleAmt");
      this.setMinMax("E33DYC", undefined, 2000000);
      this.setMinMax("E39HBE", 2500, 75000);
      this.setMinMax("E39HBP", 1, 75000);
      this.setMinMax("E5RENT", 1, 99999999);
    } else if (this.policyCode == "CO") {
      this.setMinMax("COVC", 35000, 99999999);
      this.setMinMax("E14LAO", 0, undefined);
      this.setMinMax("E16IAB", this.getLimit("COVC") * 4, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E49ITE", 0, 999999999);
      this.setMinMax("E39HBE", 2500, 75000);
      this.setMinMax("E39HBP", 1, 75000);
      this.setMinMax("E5RENT", 1, 99999999);
      this.setMinMax("ILBUSP", 1, 99999999);
      this.setMinMax("E12CCP", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E13LA1", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E10SWR", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "COR") {
      this.setMinMax("COVC", 10000, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E16IAB", this.getLimit("COVC") * 4, 99999999);
      this.setMinMax("E10FFF", 1, 999999.99);
      this.setMinMax("E5RENT", 1, 99999999);
      this.setMinMax("E12CCP", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E13LA1", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E10SWR", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "COS") {
      this.setMinMax("COVC", 10000, 99999999);
      this.setMinMax("E16IAB", this.getLimit("COVC") * 4, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E5RENT", 1, 99999999);
      this.setMinMax("E12CCP", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E13LA1", this.getLimit("COVC") * 5, undefined);
      this.setMinMax("E10SWR", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "DWC") {
      this.setMinMax("RIDDCR", 1, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
    } else if (this.policyCode == "SEAH") {
      this.setMinMax("COVA", 75000, 99999999);
      this.setMinMax("COVB", 0, 99999999);
      this.setMinMax("COVC", this.getLimit("COVA") * 0.50, 99999999);
      this.setMinMax("COVD", this.getLimit("COVA") * 0.25, 99999999);
      this.setMinMax("E10SWR", this.getDeductible("COVA"), undefined, "CoverageDeductibleAmt");
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E27CSL", 0, 99999999);
    } else if (this.policyCode == "FEC") {
      this.setMinMax("PPFEC", 0, 99999999);
      this.setMinMax("BUILA", 200000, 99999999);
      this.setMinMax("PPAR", 0, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E16IAB", 0, 99999999);
      this.setMinMax("E5RENT", 1, 99999999);
      this.setMinMax("RIDFEC", 1, 99999999);
      const fec_deduct = this.getDeductibleFromPossibleArray(['PPAR', 'BUILA', 'PPFEC', 'BUILF']);
      if (fec_deduct.deductible != 0) {
        this.setMinMax("E10SWR", fec_deduct.deductible, undefined, "CoverageDeductibleAmt");
      }
    } else if (this.policyCode == "GDC") {
      this.setMinMax("BUILA", 200000, 99999999);
    } else if (this.policyCode == "PINS") {
      this.setMinMax("PINSF", 1, 99999999);
      this.setMinMax("PINSA", 1, 99999999);
      if (this.getDeductible("PINSA") == 0) {
        this.setMinMax("E10SWR", this.getDeductible("PINSF"), undefined, "CoverageDeductibleAmt");
      } else {
        this.setMinMax("E10SWR", this.getDeductible("PINSA"), undefined, "CoverageDeductibleAmt");
      }
    } else if (this.policyCode == "SEAR") {
      this.setMinMax("BUILF", 0, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("PPFEC", 1, 99999999);
      this.setMinMax("BUILA", 25000, 99999999);
      this.setMinMax("PPAR", 1, 99999999);
      this.setMinMax("E5RENT", 1, 99999999);
      this.setMinMax("E10SWR", this.getDeductible("BUILF"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "SETP") {
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E49ITE", 0, 999999999);
      this.setMinMax("E10FFF", 1, 999999.99);
      this.setMinMax("E16IAB", this.getLimit("COVC") * 0.10, 99999999);
      this.setMinMax("E10SWR", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
      this.setMinMax("E10FFF", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "TE") {
      this.setMinMax("COVC", this.province == 'SK' ? 20000 : 25000, 99999999);
      this.setMinMax("E23FDC", 1, 99999999);
      this.setMinMax("E49ITE", 0, 999999999);
      this.setMinMax("E10FFF", 1, 999999.99);
      this.setMinMax("E16IAB", this.getLimit("COVC") * 0.10, 99999999);
      this.setMinMax("E39HBE", 2500, 75000);
      this.setMinMax("E39HBP", 1, 75000);
      this.setMinMax("ILAUTO", 1, 99999999);
      this.setMinMax("ILBOOK", 1, 99999999);
      this.setMinMax("ILBUSP", 1, 99999999);
      this.setMinMax("ILGRAV", 1, 99999999);
      this.setMinMax("ILMEQP", 1, 99999999);
      this.setMinMax("ILNURS", 1, 99999999);
      this.setMinMax("ILSOFT", 1, 99999999);
      this.setMinMax("E10SWR", this.getDeductible("COVC"), undefined, "CoverageDeductibleAmt");
    } else if (this.policyCode == "PD") {
      this.setMinMax(`${this.packageType}-COVA`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVB`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVC`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVD`, 1, 99999999);
      this.setMinMax("EQ-D", 1, 99999999);
      this.setMinMax("EQ-B", 1, 99999999);
      this.setMinMax("EQ-A", 1, 99999999);
      this.setMinMax("EQ-C", 1, 99999999);
    } else if (this.policyCode == "SD") {
      this.setMinMax(`${this.packageType}-COVA`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVB`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVC`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVD`, 1, 99999999);
      this.setMinMax("EQ-D", 1, 99999999);
      this.setMinMax("EQ-B", 1, 99999999);
      this.setMinMax("EQ-A", 1, 99999999);
      this.setMinMax("EQ-C", 1, 99999999);
    } else if (this.policyCode == "MH") {
      this.setMinMax(`${this.packageType}-COVA`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVB`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVC`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVD`, 1, 99999999);
      this.setMinMax("EQ-D", 1, 99999999);
      this.setMinMax("EQ-B", 1, 99999999);
      this.setMinMax("EQ-A", 1, 99999999);
      this.setMinMax("EQ-C", 1, 99999999);
      this.setMinMax("REBEE", 1, 200000000);
    } else if (this.policyCode == "RD") {
      this.setMinMax(`${this.packageType}-COVA`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVB`, 1, 99999999);
      this.setMinMax(`${this.packageType}-COVC`, 1, 99999999);
      this.setMinMax(`${this.packageType}-RI`, 1, 99999999);
      this.setMinMax("EQ-A", 1, 99999999);
      this.setMinMax("EQ-B", 1, 99999999);
      this.setMinMax("EQ-LC", 0, 99999999);
    } else if (this.formTypeCd == "EP") {
      this.setMinMax("PDCC", 1, 25000);
    } else if (this.tab == "liability") {
      switch (this.province) {
        case "MB":
        case "SK":
        case "AB":
          this.setMinMax("FEBILE", 10000, undefined);
          break;
        default:
          this.setMinMax("FEBILE", undefined, 99999999);

      }
    }
  }

  buildErrorMessage(field: AbstractControl) {
    this.handleMinMax();
    if (field.errors != null) {
      if (field.errors.message) {
        return field.errors.message;
      } else if (field.errors.required) {
        return 'Required';
      } else if (field.errors.requiredBy) {
        return field.errors.requiredBy.message;
      }
    }
    return null;
  }
}
